import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CodePinFields.module.scss";

export interface CodePinFieldsHandle {
	focus: () => void;
}

export interface CodePinFieldsProps {
	numberOfFields: number;
	value: string;
	obscureText?: boolean;
	/** Keyboard-driven mode: fires on every change */
	onChange?: (value: string) => void;
	/** Called when all fields are filled */
	onComplete?: () => void;
	autoFocus?: boolean;
	/** Legacy display-only click callback (used when NumberKeyboard drives input) */
	onFocus?: () => void;
}

export const CodePinFields = React.forwardRef<CodePinFieldsHandle, CodePinFieldsProps>(
	({ numberOfFields, value, obscureText = false, onChange, onComplete, autoFocus = false, onFocus }, ref) => {
		const { t } = useTranslation();
		const inputRef = useRef<HTMLInputElement>(null);
		const [focused, setFocused] = useState(false);

		useImperativeHandle(ref, () => ({
			focus: () => inputRef.current?.focus(),
		}));

		useEffect(() => {
			if (autoFocus) inputRef.current?.focus();
		}, [autoFocus]);

		useEffect(() => {
			if (value.length === numberOfFields) onComplete?.();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value, numberOfFields]);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			if (!onChange) return;
			const filtered = e.target.value.replace(/\D/g, "").slice(0, numberOfFields);
			onChange(filtered);
		};

		const handleContainerClick = () => {
			if (onChange) {
				inputRef.current?.focus();
			} else {
				onFocus?.();
			}
		};

		return (
			<div className={styles.pinFields} onClick={handleContainerClick} role="group" aria-label={t("pin.pinInputAriaLabel")}>
				{onChange && (
					<input
						ref={inputRef}
						type="text"
						inputMode="numeric"
						value={value}
						onChange={handleChange}
						onFocus={() => setFocused(true)}
						onBlur={() => setFocused(false)}
						autoComplete="off"
						aria-hidden="true"
						className={styles.pinHiddenInput}
					/>
				)}
				{Array.from({ length: numberOfFields }, (_, i) => {
					const char = value[i];
					const isActive = focused && value.length < numberOfFields && i === value.length;
					return (
						<div key={i} className={[styles.pinBox, char ? styles.pinBoxFilled : "", isActive ? styles.pinBoxActive : ""].filter(Boolean).join(" ")}>
							{char ? (obscureText ? "•" : char) : ""}
						</div>
					);
				})}
			</div>
		);
	}
);

CodePinFields.displayName = "CodePinFields";
